/**
 * The transport-to-mixer playback loop of the AnimationPlayer (ADR-0011) and GLB
 * drivers: play advances and reports, pause samples the seek, stop restores the
 * pose. `stepPlayback` decides each frame, and this hook carries it out.
 */
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { stepPlayback } from './stepPlayback';
import { startAction, seekAction } from './actionHelpers';
export function usePlaybackLoop(params) {
    const prevStateRef = useRef('stopped');
    const prevClipRef = useRef(null);
    const prevTimeRef = useRef(0);
    const prevReconfigureKeyRef = useRef(undefined);
    /** Written only by the frame below: the mixer it last drove. */
    const prevMixerRef = useRef(null);
    useFrame((_, delta) => {
        const { playState, selectedClip, transportTime, mixerRef, actionsRef } = params;
        const speedScale = params.speedScale ?? 1;
        const mixer = mixerRef.current;
        if (!mixer)
            return;
        // A rebuilt mixer, such as one that bound a late target, holds fresh actions at time 0 with
        // nothing configured, so it resumes from the transport as a clip switch would.
        const mixerRebuilt = prevMixerRef.current !== null && mixer !== prevMixerRef.current;
        prevMixerRef.current = mixer;
        const action = selectedClip ? (actionsRef.current.get(selectedClip) ?? null) : null;
        // Clip switch: stop the previous action so only one drives at a time.
        const clipChanged = selectedClip !== prevClipRef.current;
        if (clipChanged) {
            const prev = prevClipRef.current ? actionsRef.current.get(prevClipRef.current) : null;
            prev?.stop();
            prevClipRef.current = selectedClip;
        }
        // Reconfigure on a clip switch or a reconfigureKey change, without
        // restarting the running action.
        const reconfigureChanged = !Object.is(params.reconfigureKey, prevReconfigureKeyRef.current);
        if ((clipChanged || reconfigureChanged || mixerRebuilt) && action && selectedClip) {
            params.configureAction(action, selectedClip);
        }
        prevReconfigureKeyRef.current = params.reconfigureKey;
        const step = stepPlayback({
            prevState: prevStateRef.current,
            state: playState,
            // NaN differs from every time, so a paused rebuild samples its new action once.
            prevTime: mixerRebuilt ? Number.NaN : prevTimeRef.current,
            transportTime,
            liveTime: action?.time ?? null,
            clipChanged,
        });
        if (step.flushTime && action !== null) {
            params.reportTime(action.time, { immediate: true });
        }
        switch (step.command) {
            case 'ensure-playing': {
                if (action && !action.isRunning()) {
                    startAction(action);
                    if (mixerRebuilt)
                        action.time = transportTime;
                }
                mixer.update(delta * speedScale);
                if (action)
                    params.reportTime(action.time);
                break;
            }
            case 'seek': {
                if (action) {
                    seekAction(action, transportTime);
                    mixer.update(0);
                }
                break;
            }
            case 'hold-paused': {
                if (action) {
                    action.paused = true;
                }
                break;
            }
            case 'stop-and-restore': {
                mixer.stopAllAction();
                params.restore();
                break;
            }
            case 'none': {
                break;
            }
        }
        prevStateRef.current = playState;
        prevTimeRef.current = transportTime;
    });
}
//# sourceMappingURL=usePlaybackLoop.js.map